import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { Plus, X, Books } from '@phosphor-icons/react';
import ListSearch from './ListSearch';
import { ModuleShell, SkeletonBlock, EmptyState } from './module-ui';

import { BACKEND_URL, apiPath } from '../config/apiBase';
const API = apiPath('/api');

function KnowledgePage() {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    tags: '',
    published: true
  });

  useEffect(() => {
    fetchArticles();
  }, [searchQuery]);

  const fetchArticles = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = searchQuery ? { search: searchQuery } : {};
      
      // Admins, managers, and agents can see unpublished articles
      if (['super_admin', 'admin', 'manager', 'agent'].includes(user?.role)) {
        params.include_drafts = true;
      }
      
      const response = await axios.get(`${API}/knowledge`, {
        params,
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticles(response.data);
    } catch (error) {
      console.error('Failed to fetch articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateArticle = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      };
      await axios.post(`${API}/knowledge`, payload, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateModal(false);
      setFormData({ title: '', content: '', category: '', tags: '', published: true });
      fetchArticles();
    } catch (error) {
      console.error('Failed to create article:', error);
      alert('Failed to create article');
    }
  };

  const canCreateArticle = ['super_admin', 'admin', 'manager', 'agent'].includes(user?.role);

  return (
    <ModuleShell
      title="Knowledge Base"
      icon={Books}
      subtitle="Browse articles and documentation"
      headerActions={canCreateArticle ? (
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          data-testid="create-article-button"
        >
          <Plus size={18} weight="bold" />
          Create Article
        </button>
      ) : null}
    >
    <div data-testid="knowledge-page">

      <ListSearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search articles…"
        resultCount={articles.length}
        totalCount={articles.length}
      />

      {loading ? (
        <SkeletonBlock />
      ) : articles.length === 0 ? (
        <EmptyState
          icon={Books}
          title="No articles yet"
          description={canCreateArticle ? 'Create your first knowledge article to get started.' : 'No published articles are available yet.'}
          action={canCreateArticle ? (
            <button onClick={() => setShowCreateModal(true)} className="btn-primary" type="button">
              Create Article
            </button>
          ) : null}
        />
      ) : (
        <div style={{ display: 'grid', gap: '10px' }}>
          {articles.map((article) => (
            <div
              key={article.article_id}
              style={{
                background: 'white',
                border: '1px solid #E5E7EB',
                padding: '10px',
                cursor: 'pointer',
                transition: 'box-shadow 0.2s',
                opacity: article.published ? 1 : 0.7
              }}
              data-testid="article-card"
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>
                  {article.title}
                  {!article.published && (
                    <span style={{ marginLeft: '8px', fontSize: '12px', color: '#F59E0B', fontWeight: '500' }}>
                      (Draft)
                    </span>
                  )}
                </h3>
                <span className="badge">{article.category}</span>
              </div>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px', lineHeight: '1.6' }}>
                {article.content.substring(0, 200)}...
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {article.tags.map((tag, index) => (
                  <span
                    key={index}
                    style={{
                      background: '#F3F4F6',
                      color: '#4B5563',
                      padding: '4px 8px',
                      fontSize: '12px',
                      border: '1px solid #D1D5DB'
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div style={{ marginTop: '12px', fontSize: '12px', color: '#9CA3AF' }}>
                By {article.created_by_name}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        data-testid="create-article-modal"
        >
          <div style={{ background: 'white', padding: '32px', width: '100%', maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{ fontSize: '15px', fontWeight: '600' }}>Create Knowledge Article</h2>
              <button onClick={() => setShowCreateModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleCreateArticle}>
              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="input-field"
                  required
                  data-testid="article-title-input"
                />
              </div>

              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="input-field"
                  placeholder="e.g., Troubleshooting, How-To, FAQ"
                  required
                  data-testid="article-category-input"
                />
              </div>

              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Tags (comma-separated)</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="input-field"
                  placeholder="e.g., network, vpn, windows"
                  data-testid="article-tags-input"
                />
              </div>

              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Content</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="input-field"
                  rows="10"
                  required
                  data-testid="article-content-input"
                />
              </div>

              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                    data-testid="article-published-checkbox"
                  />
                  <span style={{ fontSize: '12px', fontWeight: '500' }}>Publish immediately</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="submit" className="btn-primary" style={{ flex: 1 }} data-testid="submit-article-button">
                  Create Article
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                  style={{ flex: 1 }}
                  data-testid="cancel-article-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </ModuleShell>
  );
}

export default KnowledgePage;